import { useCallback, useState } from "react";
import {
  createClientAsync,
  getClientByPhoneAsync,
} from "../api/ClientApi";
import { Client, CreateClientRequest } from "../models/ClientModels";
import { Constants } from "../constants/constants";
import type { ClientFormValues } from "../pages/organization/clients/ClientModal";

interface ClientSubmitResult {
  client: Client | null;
  message?: string;
}

interface UseClientLookupReturn {
  lookupClient: (phone: string) => Promise<Client | null>;
  clientModalOpen: boolean;
  clientPrefill: Partial<ClientFormValues> | null;
  createdClient: Client | null;
  submittingClient: boolean;
  openNewClient: (phone: string) => void;
  closeNewClient: () => void;
  clearCreatedClient: () => void;
  submitNewClient: (form: ClientFormValues) => Promise<ClientSubmitResult>;
}

export const useClientLookup = (
  organizationId: string | undefined,
): UseClientLookupReturn => {
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientPrefill, setClientPrefill] = useState<
    Partial<ClientFormValues> | null
  >(null);
  const [createdClient, setCreatedClient] = useState<Client | null>(null);
  const [submittingClient, setSubmittingClient] = useState(false);

  const lookupClient = useCallback(
    async (phone: string): Promise<Client | null> => {
      const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
      const res = await getClientByPhoneAsync(token, phone);
      return res.success && res.result ? res.result : null;
    },
    [],
  );

  const openNewClient = useCallback((phone: string) => {
    setClientPrefill(phone ? { phone } : null);
    setClientModalOpen(true);
  }, []);

  const closeNewClient = useCallback(() => {
    setClientModalOpen(false);
    setClientPrefill(null);
  }, []);

  const clearCreatedClient = useCallback(() => setCreatedClient(null), []);

  const submitNewClient = useCallback(
    async (form: ClientFormValues): Promise<ClientSubmitResult> => {
      if (!organizationId) return { client: null, message: "No active organization" };
      setSubmittingClient(true);
      try {
        const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
        const payload: CreateClientRequest = {
          organizationId,
          fullName: form.fullName,
          phone: form.phone,
          email: form.email || undefined,
          gender: form.gender || undefined,
          age: form.age ? Number(form.age) : undefined,
          address: form.address || undefined,
          city: form.city || undefined,
          area: form.area || undefined,
          notes: form.notes || undefined,
          isActive: form.isActive,
        };
        const res = await createClientAsync(token, payload);
        if (res.success && res.result) {
          setCreatedClient(res.result);
          setClientModalOpen(false);
          setClientPrefill(null);
          return { client: res.result };
        }
        return { client: null, message: res.message };
      } catch {
        return { client: null, message: "Failed to add client" };
      } finally {
        setSubmittingClient(false);
      }
    },
    [organizationId],
  );

  return {
    lookupClient,
    clientModalOpen,
    clientPrefill,
    createdClient,
    submittingClient,
    openNewClient,
    closeNewClient,
    clearCreatedClient,
    submitNewClient,
  };
};